// REACT
import React from 'react'

// MUI
import { Grid, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material'

import dayjs from 'dayjs'

import { isFreightSlotSelectableAtNow } from '../utils/helper'

// Simple weekly grid to display slot availability; purely presentational
const styles = {
    tableContainer: {
        margin: '20px',
        boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
    },
    headerRow: {
        backgroundColor: '#f5f5f5',
        fontWeight: 'bold'
    },
    cell: {
        border: '1px solid #ccc',
        height: '64px',
        width: '175px',
        boxSizing: 'border-box',
        padding: '8px',
    },
    boldText: {
        fontWeight: 'bold',
        fontSize: '14px',
        boxSizing: 'border-box',
    },
    slotLabel: {
        fontWeight: 'bold',
        fontSize: '13px',
    },
    available: {
        boxSizing: 'border-box',
        color: '#0a4d22',
        backgroundColor: '#bfe6cf',
        border: '1px solid #9dd6b6',
        cursor: 'pointer',
        height: '100%',
        width: '100%',
        borderRadius: '5px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    },
    unavailable: {
        boxSizing: 'border-box',
        color: '#777',
        backgroundColor: '#f0f0f0',
        height: '100%',
        width: '100%',
        borderRadius: '5px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    },
    pastOrClosed: {
        boxSizing: 'border-box',
        color: '#999',
        backgroundColor: '#e8e8e8',
        border: '1px solid #ccc',
        height: '100%',
        width: '100%',
        borderRadius: '5px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    },
    disabled: {
        opacity: 0.5,
        cursor: 'not-allowed'
    },
    selected: {
        outline: '3px solid rgb(2, 117, 216)'
    }
}

const WeeklySlotCalendar = ({
    weekDays, // [{ dateISO, label }]
    slots, // [{ START, END, SLOT_NUMBER }]
    availabilityByDay, // { [dateISO]: Set(slotNumber) }
    selectedCell, // { dateISO, slotNumber }
    onCellSelect,
    loading,
    disabled,
    disabledReason,
    t,
}) => {

    return (
        <TableContainer component={Paper} sx={styles.tableContainer}>
            {disabled && (
                <Grid container item sx={{ p: '12px', justifyContent: 'center' }}>
                    <Typography variant="body2">{disabledReason || 'Please complete the selection above to view availability.'}</Typography>
                </Grid>
            )}
            <Table>
                <TableHead>
                    <TableRow sx={styles.headerRow}>
                        <TableCell sx={styles.boldText}></TableCell>
                        {weekDays.map((d) => {
                            const isPastDay = dayjs(d.dateISO).startOf('day').isBefore(dayjs().startOf('day'))
                            return (
                                <TableCell key={d.dateISO} align="center" sx={{ ...styles.boldText, width: '175px', ...(isPastDay ? { opacity: 0.55, backgroundColor: '#ececec' } : {}) }}>
                                    <Typography variant="body1" sx={styles.boldText}>{d.label}</Typography>
                                    <Typography variant="body3" sx={styles.boldText}>{d.dateISO.split('-').reverse().join('-')}</Typography>
                                </TableCell>
                            )
                        })}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {slots.map((slot) => (
                        <TableRow key={slot.SLOT_NUMBER}>
                            <TableCell align="center" sx={{ ...styles.slotLabel, width: '140px' }}>{slot.START} - {slot.END}</TableCell>
                            {weekDays.map((d) => {
                                const availableSet = availabilityByDay[d.dateISO] || new Set()
                                const normalizedSlotNumber = Number(slot.SLOT_NUMBER)
                                const fromApi = availableSet.has(normalizedSlotNumber) || availableSet.has(slot.SLOT_NUMBER)
                                const timeOk = isFreightSlotSelectableAtNow(d.dateISO, slot)
                                const isBookable = fromApi && timeOk
                                const isPastBlocked = fromApi && !timeOk
                                const isSelected = selectedCell && selectedCell.dateISO === d.dateISO && (selectedCell.slotNumber === normalizedSlotNumber || selectedCell.slotNumber === slot.SLOT_NUMBER)
                                return (
                                    <TableCell key={`${d.dateISO}-${slot.SLOT_NUMBER}`} align="center" sx={styles.cell}>
                                        <Grid
                                            item
                                            sx={{
                                                ...(isBookable ? styles.available : (isPastBlocked ? styles.pastOrClosed : styles.unavailable)),
                                                ...(disabled || loading ? styles.disabled : {}),
                                                ...(isSelected && isBookable ? styles.selected : {})
                                            }}
                                            onClick={() => {
                                                if (disabled || loading || !isBookable) return
                                                onCellSelect && onCellSelect({ dateISO: d.dateISO, slot })
                                            }}
                                        >
                                            {loading ? (
                                                <span className="dot-wave" style={{ display: 'inline-block', letterSpacing: '2px' }}>●●●</span>
                                            ) : (
                                                <Typography variant="body2" sx={styles.boldText}>
                                                    {isBookable ? '✓' : (isPastBlocked ? (t?.('slotTimePassed') || '—') : '-')}
                                                </Typography>
                                            )}
                                        </Grid>
                                    </TableCell>
                                )
                            })}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
            {loading && (
                <Grid container item sx={{ p: '12px', justifyContent: 'center' }}>
                    <Typography variant="body2">Loading…</Typography>
                </Grid>
            )}
        </TableContainer>
    )
}

export default WeeklySlotCalendar


